import { toastServerError } from "helpers";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Input, Modal, Select } from "ui";

import { useCreateMarketingFlow, useUpdateMarketingFlow } from "../../apis";
import { FLOW_TRIGGER_LABELS } from "../../constants";
import { EMarketingFlowTrigger, MarketingFlow } from "../../marketing.types";

import styles from "./FlowModal.module.scss";

interface FlowModalProps {
  isOpen: boolean;
  editableFlow: MarketingFlow | null;
  onClose: () => void;
  onCreated?: (flow: MarketingFlow) => void;
}

const TRIGGER_OPTIONS = Object.values(EMarketingFlowTrigger).map(
  (trigger) => ({
    label: FLOW_TRIGGER_LABELS[trigger],
    value: trigger
  })
);

const FlowModal = ({
  isOpen,
  editableFlow,
  onClose,
  onCreated
}: FlowModalProps) => {
  const { t } = useTranslation();

  const [name, setName] = useState("");
  const [trigger, setTrigger] = useState<EMarketingFlowTrigger>(
    EMarketingFlowTrigger.Manual
  );

  useEffect(() => {
    if (isOpen) {
      setName(editableFlow?.name ?? "");
      setTrigger(editableFlow?.trigger ?? EMarketingFlowTrigger.Manual);
    }
  }, [isOpen, editableFlow]);

  const { mutate: createFlow, isLoading: isCreating } = useCreateMarketingFlow({
    onSuccess: (flow) => {
      toast.success(t("Flow created"));
      onClose();
      onCreated?.(flow);
    },
    onError: toastServerError
  });

  const { mutate: updateFlow, isLoading: isUpdating } = useUpdateMarketingFlow({
    onSuccess: () => {
      toast.success(t("Flow updated"));
      onClose();
    },
    onError: toastServerError
  });

  const handleOk = () => {
    if (!name.trim()) {
      toast.error(t("Flow name is required"));
      return;
    }

    if (editableFlow) {
      updateFlow({ flowId: editableFlow.id, data: { name: name.trim(), trigger } });
    } else {
      createFlow({ name: name.trim(), trigger });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      title={editableFlow ? t("Edit flow") : t("New flow")}
      contentClassName={styles.modalContent}
      isLoading={isCreating || isUpdating}
      okText={editableFlow ? t("Save") : t("Create")}
      onClose={onClose}
      onOk={handleOk}
    >
      <div className={styles.form}>
        <Input
          label={t("Flow name")}
          placeholder={t("e.g. Post-purchase follow-up")}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />

        <Select
          label={t("Trigger")}
          options={TRIGGER_OPTIONS.map((option) => ({
            ...option,
            label: t(option.label)
          }))}
          value={trigger}
          onChange={(selected) =>
            setTrigger(selected as EMarketingFlowTrigger)
          }
        />

        <span className={styles.form__hint}>
          {t(
            "Auto triggers enroll contacts when the event fires (order placed, app signup, prospect entered). Manual flows only enroll contacts you add explicitly."
          )}
        </span>
      </div>
    </Modal>
  );
};

export default FlowModal;
